import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { MeetingRoom, Device, Company, Settings } from '../types';
import { useSettings } from '../context/SettingsContext';
import { formatHour } from '../utils/time';
import { useConfirm } from '../context/ConfirmContext';

const COMMON_AMENITIES = [
  'Projector',
  'Whiteboard',
  'Video Conferencing',
  'TV Screen',
  'Conference Phone',
  'Air Conditioning',
  'Standing Desk',
  'Catering Service',
  'Recording Equipment',
  'Microphones',
  'Multiple Screens'
];

export function AdminRoomsPage() {
  const { timeFormat } = useSettings();
  const showConfirm = useConfirm();
  const [rooms, setRooms] = useState<MeetingRoom[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<MeetingRoom | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    capacity: 4,
    amenities: [] as string[],
    floor: '',
    address: '',
    description: '',
    quickBookDurations: [30, 60, 90, 120] as number[],
    lockedToCompanyIds: [] as string[],
    openingHour: null as number | null,
    closingHour: null as number | null,
    bookingEmail: '' as string,
    imapHost: '' as string,
    imapPort: 993 as number,
    imapUser: '' as string,
    imapPass: '' as string,
    imapMailbox: '' as string,
    smtpHost: '' as string,
    smtpPort: 587 as number,
    smtpSecure: false as boolean,
    calendarFeedEnabled: true as boolean,
  });
  // Track whether the user typed a new IMAP password (empty = keep existing)
  const [imapPassChanged, setImapPassChanged] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Device management state
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<MeetingRoom | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [deviceError, setDeviceError] = useState('');
  const [savingDevice, setSavingDevice] = useState(false);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);
  const [imapStatuses, setImapStatuses] = useState<Record<string, { status: 'ok' | 'error' | 'unknown'; lastChecked: string | null; lastError: string | null }>>({});

  useEffect(() => {
    loadData();
    // Refresh IMAP statuses every 30 seconds
    const interval = setInterval(loadImapStatuses, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const [roomsData, companiesData, settingsData] = await Promise.all([
        api.getRooms(true),
        api.getCompanies(),
        api.getSettings()
      ]);
      setRooms(roomsData);
      setCompanies(companiesData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
    // Load IMAP statuses after rooms are loaded (non-blocking)
    loadImapStatuses();
  };

  const loadImapStatuses = async () => {
    try {
      const statuses = await api.getRoomImapStatuses();
      setImapStatuses(statuses);
    } catch {
      // Non-critical — silently ignore
    }
  };

  const loadRooms = async () => {
    try {
      const data = await api.getRooms(true);
      setRooms(data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const handleOpenModal = (room?: MeetingRoom) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        name: room.name,
        capacity: room.capacity,
        amenities: room.amenities,
        floor: room.floor,
        address: room.address,
        description: room.description,
        quickBookDurations: room.quickBookDurations || [30, 60, 90, 120],
        lockedToCompanyIds: room.lockedToCompanyIds || [],
        openingHour: room.openingHour ?? null,
        closingHour: room.closingHour ?? null,
        bookingEmail: room.bookingEmail || '',
        imapHost: room.imapHost || '',
        imapPort: room.imapPort || 993,
        imapUser: room.imapUser || '',
        imapPass: '',  // Never pre-fill — user must type to change
        imapMailbox: room.imapMailbox || '',
        smtpHost: room.smtpHost || '',
        smtpPort: room.smtpPort || 587,
        smtpSecure: room.smtpSecure ?? false,
        calendarFeedEnabled: room.calendarFeedEnabled !== false,
      });
    } else {
      setEditingRoom(null);
      setFormData({
        name: '',
        capacity: 4,
        amenities: [],
        floor: '',
        address: '',
        description: '',
        quickBookDurations: [30, 60, 90, 120],
        lockedToCompanyIds: [],
        openingHour: null,
        closingHour: null,
        bookingEmail: '',
        imapHost: '',
        imapPort: 993,
        imapUser: '',
        imapPass: '',
        imapMailbox: '',
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: false,
        calendarFeedEnabled: true,
      });
    }
    setImapPassChanged(false);
    setError('');
    setShowModal(true);
  };

  const handleAmenityToggle = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleCompanyToggle = (companyId: string) => {
    setFormData(prev => ({
      ...prev,
      lockedToCompanyIds: prev.lockedToCompanyIds.includes(companyId)
        ? prev.lockedToCompanyIds.filter(id => id !== companyId)
        : [...prev.lockedToCompanyIds, companyId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const imapConfigured = formData.imapHost.trim() && formData.imapUser.trim();
      const roomData: any = {
        ...formData,
        lockedToCompanyIds: formData.lockedToCompanyIds.length > 0 ? formData.lockedToCompanyIds : [],
        bookingEmail: formData.bookingEmail.trim() || null,
        imapHost: formData.imapHost.trim() || null,
        imapPort: formData.imapPort || 993,
        imapUser: formData.imapUser.trim() || null,
        imapMailbox: formData.imapMailbox.trim() || null,
        smtpHost: formData.smtpHost.trim() || null,
        smtpPort: formData.smtpPort || 587,
        smtpSecure: formData.smtpSecure,
      };
      // Only include imapPass if user typed a new one
      if (imapPassChanged && formData.imapPass) {
        roomData.imapPass = formData.imapPass;
      } else if (!editingRoom && imapConfigured) {
        // Creating a new room with IMAP — password is required
        roomData.imapPass = formData.imapPass || null;
      }
      if (editingRoom) {
        await api.updateRoom(editingRoom.id, roomData);
      } else {
        await api.createRoom(roomData);
      }
      setShowModal(false);
      loadRooms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save room');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (room: MeetingRoom) => {
    try {
      await api.updateRoom(room.id, { isActive: !room.isActive });
      loadRooms();
    } catch (error) {
      console.error('Failed to update room:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await showConfirm({ message: 'Are you sure you want to permanently delete this room? This action cannot be undone.', title: 'Delete Room', confirmLabel: 'Delete' })) return;

    try {
      await api.deleteRoom(id, false);
      loadRooms();
    } catch (error) {
      console.error('Failed to delete room:', error);
    }
  };

  // Device management functions
  const handleOpenDevicesModal = async (room: MeetingRoom) => {
    setSelectedRoom(room);
    setShowDevicesModal(true);
    setShowAddDevice(false);
    setNewDeviceName('');
    setDeviceError('');
    await loadDevices(room.id);
  };

  const loadDevices = async (roomId: string) => {
    setLoadingDevices(true);
    try {
      const data = await api.getDevicesByRoom(roomId);
      setDevices(data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleAddDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRoom || !newDeviceName.trim()) return;

    setDeviceError('');
    setSavingDevice(true);

    try {
      await api.createDevice({
        name: newDeviceName.trim(),
        roomId: selectedRoom.id
      });
      setNewDeviceName('');
      setShowAddDevice(false);
      await loadDevices(selectedRoom.id);
    } catch (err) {
      setDeviceError(err instanceof Error ? err.message : 'Failed to add device');
    } finally {
      setSavingDevice(false);
    }
  };

  const handleToggleDeviceActive = async (device: Device) => {
    if (!selectedRoom) return;

    try {
      await api.updateDevice(device.id, { isActive: !device.isActive });
      await loadDevices(selectedRoom.id);
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  const handleRegenerateToken = async (device: Device) => {
    if (!selectedRoom) return;
    if (!await showConfirm({ message: `Regenerate token for "${device.name}"? The old token will stop working immediately.`, title: 'Regenerate Token', confirmLabel: 'Regenerate', variant: 'warning' })) return;

    try {
      await api.regenerateDeviceToken(device.id);
      await loadDevices(selectedRoom.id);
    } catch (error) {
      console.error('Failed to regenerate token:', error);
    }
  };

  const handleDeleteDevice = async (device: Device) => {
    if (!selectedRoom) return;
    if (!await showConfirm({ message: `Delete device "${device.name}"? This action cannot be undone.`, title: 'Delete Device', confirmLabel: 'Delete' })) return;

    try {
      await api.deleteDevice(device.id);
      await loadDevices(selectedRoom.id);
    } catch (error) {
      console.error('Failed to delete device:', error);
    }
  };

  const handleCopyToken = async (device: Device) => {
    try {
      await navigator.clipboard.writeText(device.token);
      setCopiedTokenId(device.id);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (error) {
      console.error('Failed to copy token:', error);
    }
  };

  const formatLastSeen = (lastSeenAt: string | null) => {
    if (!lastSeenAt) return 'Never';
    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return <div className="loading">Loading rooms...</div>;
  }

  return (
    <div className="admin-rooms-page">
      <div className="page-header">
        <h1>Manage Meeting Rooms</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          Add Room
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Capacity</th>
              <th>Floor</th>
              <th>Amenities</th>
              <th>Access</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rooms.map(room => (
              <tr key={room.id} className={!room.isActive ? 'inactive' : ''}>
                <td>
                  <div>{room.name}</div>
                  {room.bookingEmail && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {room.imapHost && (() => {
                        const s = imapStatuses[room.id];
                        if (!s || s.status === 'unknown') return (
                          <span title="IMAP: waiting for first poll…" style={{ color: '#9ca3af', fontSize: '0.7rem' }}>●</span>
                        );
                        if (s.status === 'ok') return (
                          <span title={`IMAP connected — last checked ${s.lastChecked ? new Date(s.lastChecked).toLocaleTimeString() : '?'}`} style={{ color: '#10b981' }}>✓</span>
                        );
                        return (
                          <span title={`IMAP error: ${s.lastError ?? 'unknown'}`} style={{ color: '#ef4444', cursor: 'help' }}>✗</span>
                        );
                      })()}
                      {room.bookingEmail}
                    </div>
                  )}
                </td>
                <td>{room.capacity}</td>
                <td>{room.floor}</td>
                <td>
                  <div className="amenities-cell">
                    {room.amenities.slice(0, 3).map(a => (
                      <span key={a} className="amenity-tag small">{a}</span>
                    ))}
                    {room.amenities.length > 3 && (
                      <span className="amenity-tag small">+{room.amenities.length - 3} more</span>
                    )}
                  </div>
                </td>
                <td>
                  {(!room.lockedToCompanyIds || room.lockedToCompanyIds.length === 0) ? (
                    <span className="access-badge open">Open to All</span>
                  ) : (
                    <div className="access-companies">
                      {room.lockedToCompanyIds.slice(0, 2).map(id => {
                        const company = companies.find(c => c.id === id);
                        return (
                          <span key={id} className="access-badge restricted">
                            {company?.name || 'Unknown'}
                          </span>
                        );
                      })}
                      {room.lockedToCompanyIds.length > 2 && (
                        <span className="access-badge restricted">
                          +{room.lockedToCompanyIds.length - 2} more
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${room.isActive ? 'active' : 'inactive'}`}>
                    {room.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => handleOpenModal(room)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-small btn-info"
                      onClick={() => handleOpenDevicesModal(room)}
                    >
                      Devices
                    </button>
                    <button
                      className={`btn btn-small ${room.isActive ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleActive(room)}
                    >
                      {room.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(room.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Room Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRoom ? 'Edit Room' : 'Add Room'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Room Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="e.g., Innovation Lab"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="capacity">Capacity *</label>
                    <input
                      type="number"
                      id="capacity"
                      value={formData.capacity}
                      onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                      required
                      min={1}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="floor">Floor *</label>
                  <input
                    type="text"
                    id="floor"
                    value={formData.floor}
                    onChange={e => setFormData({ ...formData, floor: e.target.value })}
                    required
                    placeholder="e.g., 3rd Floor"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address *</label>
                  <input
                    type="text"
                    id="address"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    required
                    placeholder="Full address including room number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the room"
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bookingEmail">Booking Email Address</label>
                  <input
                    type="email"
                    id="bookingEmail"
                    value={formData.bookingEmail}
                    onChange={e => setFormData({ ...formData, bookingEmail: e.target.value })}
                    placeholder="e.g., boardroom@rooms.yourdomain.com"
                    maxLength={254}
                  />
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    The email address users send calendar invites to. Also used as the reply address for iMIP responses.
                  </small>
                </div>

                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.5rem' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.95rem' }}>
                    IMAP Inbox (for receiving booking requests)
                  </legend>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '0.75rem' }}>
                    Connect this room's mailbox so incoming calendar invites are processed automatically.
                    Leave all fields empty to disable email-based booking for this room.
                  </small>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: 3 }}>
                      <label htmlFor="imapHost">IMAP Host</label>
                      <input
                        type="text"
                        id="imapHost"
                        value={formData.imapHost}
                        onChange={e => setFormData({ ...formData, imapHost: e.target.value })}
                        placeholder="e.g., imap.gmail.com"
                        maxLength={253}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label htmlFor="imapPort">Port</label>
                      <input
                        type="number"
                        id="imapPort"
                        value={formData.imapPort}
                        onChange={e => setFormData({ ...formData, imapPort: parseInt(e.target.value) || 993 })}
                        min={1}
                        max={65535}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="imapUser">IMAP Username</label>
                    <input
                      type="text"
                      id="imapUser"
                      value={formData.imapUser}
                      onChange={e => setFormData({ ...formData, imapUser: e.target.value })}
                      placeholder="e.g., boardroom@rooms.yourdomain.com"
                      maxLength={254}
                      autoComplete="off"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="imapPass">IMAP Password</label>
                    <input
                      type="password"
                      id="imapPass"
                      value={formData.imapPass}
                      onChange={e => {
                        setFormData({ ...formData, imapPass: e.target.value });
                        setImapPassChanged(true);
                      }}
                      placeholder={editingRoom?.hasImapPassword ? '(password saved — type to change)' : 'Enter password'}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="imapMailbox">Mailbox / Folder</label>
                    <input
                      type="text"
                      id="imapMailbox"
                      value={formData.imapMailbox}
                      onChange={e => setFormData({ ...formData, imapMailbox: e.target.value })}
                      placeholder="INBOX"
                      maxLength={255}
                    />
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      Leave blank to use INBOX. Use TLS port 993 (recommended).
                    </small>
                  </div>
                </fieldset>

                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.5rem' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.95rem' }}>
                    SMTP Outbox (for sending booking replies)
                  </legend>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '0.75rem' }}>
                    Used to send ACCEPT / DECLINE replies from the room's own address. Uses the same username and password as IMAP.
                    On most providers (including Hetzner) the SMTP host is the same as the IMAP host — leave the host blank to reuse it.
                  </small>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: 3 }}>
                      <label htmlFor="smtpHost">SMTP Host (optional)</label>
                      <input
                        type="text"
                        id="smtpHost"
                        value={formData.smtpHost}
                        onChange={e => setFormData({ ...formData, smtpHost: e.target.value })}
                        placeholder={formData.imapHost || 'Same as IMAP host'}
                        maxLength={253}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label htmlFor="smtpPort">Port</label>
                      <input
                        type="number"
                        id="smtpPort"
                        value={formData.smtpPort}
                        onChange={e => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
                        min={1}
                        max={65535}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.smtpSecure}
                        onChange={e => setFormData({ ...formData, smtpSecure: e.target.checked })}
                      />
                      Use SSL/TLS (port 465) — leave unchecked for STARTTLS (port 587)
                    </label>
                  </div>
                </fieldset>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.calendarFeedEnabled}
                      onChange={e => setFormData({ ...formData, calendarFeedEnabled: e.target.checked })}
                    />
                    Allow calendar feed subscriptions (ICS)
                  </label>
                  <small>When disabled, users cannot subscribe to this room's ICS feed and existing feed URLs will stop working.</small>
                </div>

                <div className="form-group">
                  <label>Quick Book Durations (for device screen)</label>
                  <div className="quick-book-durations">
                    {[15, 30, 45, 60, 90, 120, 180, 240].map(duration => (
                      <label key={duration} className="duration-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.quickBookDurations.includes(duration)}
                          onChange={() => {
                            setFormData(prev => ({
                              ...prev,
                              quickBookDurations: prev.quickBookDurations.includes(duration)
                                ? prev.quickBookDurations.filter(d => d !== duration)
                                : [...prev.quickBookDurations, duration].sort((a, b) => a - b)
                            }));
                          }}
                        />
                        <span>{duration < 60 ? `${duration} min` : `${duration / 60} ${duration === 60 ? 'hour' : 'hours'}`}</span>
                      </label>
                    ))}
                  </div>
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    Select the booking durations available on the room's display device (max 4 will be shown)
                  </small>
                </div>

                <div className="form-group">
                  <label>Amenities</label>
                  <div className="amenities-grid">
                    {COMMON_AMENITIES.map(amenity => (
                      <label key={amenity} className="amenity-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                        />
                        <span>{amenity}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '1rem', marginBottom: '0.5rem' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.95rem' }}>
                    Booking Hours
                  </legend>
                  <small style={{ color: '#6b7280', display: 'block', marginBottom: '0.75rem' }}>
                    Override the global booking hours for this room. Leave empty to use the global setting.
                  </small>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="roomOpeningHour">Opening Hour</label>
                      <select
                        id="roomOpeningHour"
                        value={formData.openingHour ?? ''}
                        onChange={e => setFormData({ ...formData, openingHour: e.target.value === '' ? null : Number(e.target.value) })}
                      >
                        <option value="">Use Global{settings ? ` (${formatHour(settings.openingHour, timeFormat)})` : ''}</option>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{formatHour(i, timeFormat)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="roomClosingHour">Closing Hour</label>
                      <select
                        id="roomClosingHour"
                        value={formData.closingHour ?? ''}
                        onChange={e => setFormData({ ...formData, closingHour: e.target.value === '' ? null : Number(e.target.value) })}
                      >
                        <option value="">Use Global{settings ? ` (${formatHour(settings.closingHour, timeFormat)})` : ''}</option>
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>{formatHour(i, timeFormat)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </fieldset>

                <div className="form-group">
                  <label>Restrict Access to Companies</label>
                  <small style={{ color: '#6b7280', marginBottom: '0.5rem', display: 'block' }}>
                    Select companies that can book this room. Leave empty to allow all companies.
                  </small>
                  {companies.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No companies available</p>
                  ) : (
                    <div className="companies-grid">
                      {companies.map(company => (
                        <label key={company.id} className="company-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.lockedToCompanyIds.includes(company.id)}
                            onChange={() => handleCompanyToggle(company.id)}
                          />
                          <span>{company.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Room'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Devices Modal */}
      {showDevicesModal && selectedRoom && (
        <div className="modal-overlay" onClick={() => setShowDevicesModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Screen Devices - {selectedRoom.name}</h2>
              <button className="modal-close" onClick={() => setShowDevicesModal(false)} aria-label="Close">×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Screen devices can be placed outside meeting rooms to show room status and allow quick bookings.
                Each device uses a unique token for authentication.
              </p>

              {deviceError && <div className="alert alert-error">{deviceError}</div>}

              {loadingDevices ? (
                <div className="loading">Loading devices...</div>
              ) : (
                <>
                  {devices.length === 0 && !showAddDevice ? (
                    <div className="empty-state">
                      <p>No devices linked to this room yet.</p>
                    </div>
                  ) : (
                    <div className="devices-list">
                      {devices.map(device => (
                        <div key={device.id} className={`device-card ${!device.isActive ? 'inactive' : ''}`}>
                          <div className="device-info">
                            <div className="device-name">
                              {device.name}
                              <span className={`status-badge small ${device.isActive ? 'active' : 'inactive'}`}>
                                {device.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="device-meta">
                              Last seen: {formatLastSeen(device.lastSeenAt)}
                            </div>
                            <div className="device-token">
                              <code>{device.token.substring(0, 16)}...{device.token.substring(device.token.length - 8)}</code>
                              <button
                                className="btn btn-tiny"
                                onClick={() => handleCopyToken(device)}
                                title="Copy full token"
                              >
                                {copiedTokenId === device.id ? 'Copied!' : 'Copy'}
                              </button>
                            </div>
                          </div>
                          <div className="device-actions">
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => handleRegenerateToken(device)}
                              title="Generate new token"
                            >
                              Regenerate Token
                            </button>
                            <button
                              className={`btn btn-small ${device.isActive ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleToggleDeviceActive(device)}
                            >
                              {device.isActive ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteDevice(device)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddDevice ? (
                    <form onSubmit={handleAddDevice} className="add-device-form">
                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label htmlFor="deviceName">Device Name</label>
                          <input
                            type="text"
                            id="deviceName"
                            value={newDeviceName}
                            onChange={e => setNewDeviceName(e.target.value)}
                            placeholder="e.g., Screen Outside Room 101"
                            required
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowAddDevice(false);
                            setNewDeviceName('');
                            setDeviceError('');
                          }}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={savingDevice}>
                          {savingDevice ? 'Adding...' : 'Add Device'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAddDevice(true)}
                      style={{ marginTop: '1rem' }}
                    >
                      Add Device
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDevicesModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
